import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaMapMarkerAlt,
  FaBriefcase,
  FaEye,
  FaHeart,
  FaLock,
  FaUserTie,
  FaSearch,
  FaFilter
} from 'react-icons/fa';

// Mock candidate data for preview (blurred)
const MOCK_CANDIDATES = [
  {
    id: 1,
    firstName: 'John',
    lastName: 'Smith',
    parish: 'Kingston',
    employmentType: 'Full-Time',
    skills: 'Marketing, Sales',
    aboutMe: 'Experienced marketing professional with over 5 years in digital marketing and brand management...'
  },
  {
    id: 2,
    firstName: 'Sarah',
    lastName: 'Johnson',
    parish: 'Saint Andrew',
    employmentType: 'Part-Time',
    skills: 'Accounting, Finance',
    aboutMe: 'Detail-oriented accountant with expertise in financial reporting and tax preparation...'
  },
  {
    id: 3,
    firstName: 'Michael',
    lastName: 'Williams',
    parish: 'Saint Catherine',
    employmentType: 'Contract',
    skills: 'IT Support, Networking',
    aboutMe: 'Certified IT professional specializing in network administration and technical support...'
  },
  {
    id: 4,
    firstName: 'Emily',
    lastName: 'Brown',
    parish: 'Saint James',
    employmentType: 'Full-Time',
    skills: 'Customer Service, Retail',
    aboutMe: 'Customer-focused professional with excellent communication skills and retail experience...'
  },
  {
    id: 5,
    firstName: 'David',
    lastName: 'Davis',
    parish: 'Manchester',
    employmentType: 'Internship',
    skills: 'Graphic Design, Adobe',
    aboutMe: 'Creative graphic designer proficient in Adobe Creative Suite and brand identity design...'
  },
  {
    id: 6,
    firstName: 'Jessica',
    lastName: 'Miller',
    parish: 'Clarendon',
    employmentType: 'Full-Time',
    skills: 'Nursing, Healthcare',
    aboutMe: 'Compassionate registered nurse with experience in patient care and clinical settings...'
  }
];

const CandidatesPreview = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-100 pt-24 pb-12">
      <Helmet>
        <title>Browse Job Candidates in Jamaica | HireMeJA</title>
        <meta name="description" content="Find skilled candidates across Jamaica. Browse profiles of job seekers in Kingston, Montego Bay, and all 14 parishes ready to be hired." />
        <link rel="canonical" href="https://hiremeja.com/all-candidates" />
      </Helmet>
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-blue-950">Candidate Database</h1>
              <p className="text-gray-600 mt-1">Browse qualified candidates for your job openings</p>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <FaUserTie className="text-blue-950" />
              <span>500+ Active Candidates</span>
            </div>
          </div>

          {/* Mock Search/Filter Bar (disabled) */}
          <div className="mt-6 flex flex-col md:flex-row gap-4 opacity-50 pointer-events-none">
            <div className="flex-1 relative">
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, skills, or location..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg bg-gray-50"
                disabled
              />
            </div>
            <button className="flex items-center gap-2 px-4 py-2 border rounded-lg bg-gray-50" disabled>
              <FaFilter />
              Filters
            </button>
          </div>
        </div>

        {/* Blurred Candidates Grid with Overlay */}
        <div className="relative">
          {/* Sign-in Overlay */}
          <div className="absolute inset-0 z-20 flex items-center justify-center">
            <div className="bg-white rounded-xl shadow-2xl p-8 mx-4 max-w-md text-center border-2 border-blue-950">
              <div className="w-16 h-16 bg-blue-950 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaLock className="text-white text-2xl" />
              </div>
              <h2 className="text-2xl font-bold text-blue-950 mb-3">
                Unlock Full Access
              </h2>
              <p className="text-gray-600 mb-6">
                Sign into your Employer Profile to access our complete database of qualified candidates, view resumes, and connect with top talent.
              </p>
              <div className="space-y-3">
                <Link
                  to="/employer-sign-in"
                  className="block w-full py-3 px-6 bg-blue-950 text-white rounded-lg hover:bg-blue-800 transition-colors font-semibold"
                >
                  Sign In as Employer
                </Link>
                <Link
                  to="/employer-registration"
                  className="block w-full py-3 px-6 bg-[#cddd3a] text-blue-950 rounded-lg hover:bg-yellow-400 transition-colors font-semibold"
                >
                  Register as Employer
                </Link>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                Already a candidate?{' '}
                <Link to="/candidate-sign-in" className="text-blue-950 hover:underline">
                  Sign in here
                </Link>
              </p>
            </div>
          </div>

          {/* Blurred Content */}
          <div className="filter blur-sm pointer-events-none select-none">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100 border-b-2 border-gray-300">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Photo</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Location</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Employment Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Skills</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">About</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {MOCK_CANDIDATES.map((candidate) => (
                      <tr key={candidate.id} className="border-b hover:bg-gray-50">
                        {/* Photo */}
                        <td className="px-4 py-4">
                          <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center">
                            <span className="text-gray-500 text-lg font-semibold">
                              {candidate.firstName[0]}{candidate.lastName[0]}
                            </span>
                          </div>
                        </td>

                        {/* Name */}
                        <td className="px-4 py-4">
                          <span className="font-semibold text-blue-950">
                            {candidate.firstName} {candidate.lastName}
                          </span>
                        </td>

                        {/* Location */}
                        <td className="px-4 py-4 text-sm text-gray-700">
                          <div className="flex items-center">
                            <FaMapMarkerAlt className="mr-2 text-blue-950" size={14} />
                            <span>{candidate.parish}</span>
                          </div>
                        </td>

                        {/* Employment Type */}
                        <td className="px-4 py-4 text-sm text-gray-700">
                          <div className="flex items-center">
                            <FaBriefcase className="mr-2 text-blue-950" size={14} />
                            <span>{candidate.employmentType}</span>
                          </div>
                        </td>

                        {/* Skills */}
                        <td className="px-4 py-4 text-sm text-gray-700">
                          <span>{candidate.skills}</span>
                        </td>

                        {/* About */}
                        <td className="px-4 py-4 text-sm text-gray-600 max-w-xs">
                          <p className="line-clamp-2">{candidate.aboutMe}</p>
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <button className="inline-flex items-center px-3 py-2 bg-blue-950 text-white text-sm rounded-md">
                              <FaEye className="mr-1" size={14} />
                              View
                            </button>
                            <button className="p-2 text-gray-400">
                              <FaHeart size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mock Pagination */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
                <span className="text-sm text-gray-600">Showing 1-6 of 500+ candidates</span>
                <div className="flex gap-2">
                  <button className="px-3 py-1 border rounded bg-gray-100">Previous</button>
                  <button className="px-3 py-1 border rounded bg-blue-950 text-white">1</button>
                  <button className="px-3 py-1 border rounded bg-gray-100">2</button>
                  <button className="px-3 py-1 border rounded bg-gray-100">3</button>
                  <button className="px-3 py-1 border rounded bg-gray-100">Next</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-blue-950 mb-4">Why Hire Through HireMeJA?</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <FaUserTie className="text-blue-950 text-xl" />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">Verified Candidates</h4>
              <p className="text-sm text-gray-600">Access pre-screened candidates with verified profiles and resumes.</p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <FaSearch className="text-blue-950 text-xl" />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">Advanced Search</h4>
              <p className="text-sm text-gray-600">Filter by location, skills, experience, and employment type.</p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <FaBriefcase className="text-blue-950 text-xl" />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">Direct Contact</h4>
              <p className="text-sm text-gray-600">Reach out directly to candidates and invite them to apply.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidatesPreview;
